import { useRef } from "react";

import { formatInteger } from "@/shared/utils/format";

import { BRAZIL_MAP_VIEWBOX, BRAZIL_STATES } from "./brazil-states";
import { useChartTip } from "./chart-kit";
import { EmptyState } from "./EmptyState";
import { heatLevel } from "./IntensityHeatmap";

/** Siglas que não cabem dentro do contorno no tamanho do mapa. */
const SMALL_STATES = new Set(["DF", "SE", "AL", "RN", "PB", "PE", "ES", "RJ"]);

/** A UF como o cadastro a guarda: duas letras em caixa alta. "São Paulo"
 *  escrito por extenso (o legado tem grafia livre) não vira sigla aqui —
 *  conta em "fora do mapa". */
export function stateCode(value: string): string | null {
  const code = value.trim().toUpperCase();
  return BRAZIL_STATES.some(shape => shape.uf === code) ? code : null;
}

/**
 * Exposição por estado: o mapa do Brasil pintado pela quantidade de
 * menções de imprensa de cada UF (o veículo dá a UF), num tom só, com a
 * lista ao lado pra quem precisa do número. O que não é sigla de estado
 * (UF não informada, grafia por extenso) aparece à parte, nunca some.
 */
export function BrazilMap({ items }: { items: Array<{ key: string; count: number }> }) {
  const frame = useRef<HTMLDivElement>(null);
  const { bind, tip } = useChartTip(frame);
  const byState = new Map<string, number>();
  let outside = 0;
  for (const item of items) {
    const code = stateCode(item.key);
    if (code) byState.set(code, (byState.get(code) ?? 0) + item.count);
    else outside += item.count;
  }
  const max = Math.max(...byState.values(), 0);
  if (!max && !outside) return <EmptyState label="Sem UF nas menções de imprensa"/>;
  const ranked = [...byState.entries()].sort((a, b) => b[1] - a[1]);
  const total = ranked.reduce((sum, [, count]) => sum + count, 0) + outside;
  return (
    <div className="chart-frame chart-map-layout" ref={frame}>
      <svg data-export viewBox={BRAZIL_MAP_VIEWBOX} className="chart-map" role="img" aria-label="Menções de imprensa por estado">
        {BRAZIL_STATES.map(shape => {
          const count = byState.get(shape.uf) ?? 0;
          const level = heatLevel(count, max);
          return (
            <g key={shape.uf}>
              <path
                d={shape.d}
                className={`chart-map-state chart-seq-${level}`}
                tabIndex={count ? 0 : -1}
                aria-label={`${shape.name}: ${formatInteger(count)} menções`}
                {...bind({ title: shape.name, lines: [{ label: "Menções", value: formatInteger(count) }] })}
              />
              {!SMALL_STATES.has(shape.uf) && (
                <text x={shape.cx} y={shape.cy} className={`chart-map-code${level >= 4 ? " is-light" : ""}`} textAnchor="middle" dominantBaseline="middle">{shape.uf}</text>
              )}
            </g>
          );
        })}
      </svg>
      {tip}
      <ol className="chart-map-ranking">
        {ranked.slice(0, 10).map(([code, count]) => (
          <li key={code}>
            <span className={`chart-legend-swatch chart-seq-${heatLevel(count, max)}`} aria-hidden="true"/>
            <span>{BRAZIL_STATES.find(shape => shape.uf === code)?.name ?? code}</span>
            <strong>{formatInteger(count)}</strong>
            <small>{Math.round((count / total) * 100)}%</small>
          </li>
        ))}
        {outside > 0 && (
          <li className="is-outside">
            <span className="chart-legend-swatch chart-tone--cat-other" aria-hidden="true"/>
            <span>Sem UF ou fora do mapa</span>
            <strong>{formatInteger(outside)}</strong>
            <small>{Math.round((outside / total) * 100)}%</small>
          </li>
        )}
      </ol>
    </div>
  );
}
